import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Upload, X, Loader, Image as ImageIcon } from 'lucide-react';
import { getImageUrl } from '../utils/imageUrl';
import './ImageUpload.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImageUpload = ({ onUploadSuccess, currentImageUrl = '', resetAfterUpload = false }) => {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (max 25MB for high resolution)
    if (file.size > 25 * 1024 * 1024) {
      setError('File size must be less than 25MB');
      return;
    }

    setError('');
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/admin/upload`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      // Use relative path for storage (works in production)
      let uploadedUrl = response.data.url;
      // Ensure we use the /api/uploads/ path which is routed to backend
      if (uploadedUrl.startsWith('/uploads/')) {
        uploadedUrl = `/api${uploadedUrl}`;
      } else if (!uploadedUrl.startsWith('/api/uploads/') && !uploadedUrl.startsWith('http')) {
        const filename = uploadedUrl.split('/').pop();
        uploadedUrl = `/api/uploads/${filename}`;
      }
      
      setPreviewUrl(resetAfterUpload ? '' : uploadedUrl);
      onUploadSuccess(uploadedUrl);
      if (resetAfterUpload && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = () => {
    setPreviewUrl('');
    onUploadSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="image-upload-container">
      {previewUrl ? (
        <div className="image-preview-container">
          <img src={getImageUrl(previewUrl)} alt="Preview" className="uploaded-image-preview" />
          <button
            type="button"
            onClick={removeImage}
            className="remove-image-btn"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          className={`upload-area ${dragActive ? 'drag-active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleChange}
            style={{ display: 'none' }}
          />
          
          {uploading ? (
            <div className="upload-status">
              <Loader size={32} className="spinner" />
              <p>Uploading... {uploadProgress}%</p>
              <div className="upload-progress-bar">
                <div 
                  className="upload-progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          ) : (
            <div className="upload-prompt">
              <ImageIcon size={48} />
              <p className="upload-title">Click to upload or drag and drop</p>
              <p className="upload-subtitle">PNG, JPG, GIF or WebP (Max 25MB)</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="upload-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default ImageUpload;
